import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Package,
    Boxes,
    ShoppingBag,
    Image as ImageIcon,
    Film,
    Tag,
    Users,
    BarChart3,
    LogOut,
    Eye,
    FileText,
    Star,
    Truck,
    Layers,
    Folders,
    SlidersHorizontal,
    PackageSearch,
    Settings as SettingsIcon,
    Sparkles,
    MessageCircle,
    Gift,
    Mail,
    Menu as MenuIcon,
    Search,
    Receipt,
    Building2,
    BadgeIndianRupee,
    UserCog,
    Activity,
    LineChart,
    UndoDot,
    Network,
    ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminTopBar from "./AdminTopBar";
import { useState } from "react";

const LOGO_BLACK = "/xplosiv-logo-removebg-preview.png";

const SECTIONS = [
    {
        label: "Overview",
        links: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
    },
    {
        label: "Experiences",
        links: [
            { to: "/admin/products", label: "Event packages", icon: Package },
            { to: "/admin/occasions", label: "Occasion galleries", icon: ImageIcon },
            { to: "/admin/categories", label: "Event types", icon: Folders },
            { to: "/admin/collections", label: "Experience collections", icon: Layers },
            { to: "/admin/attributes", label: "Package details", icon: SlidersHorizontal },
            { to: "/admin/inventory", label: "Package availability", icon: Boxes },
        ],
    },
    {
        label: "Event operations",
        links: [
            { to: "/admin/orders", label: "Bookings", icon: ShoppingBag },
            { to: "/admin/shipments", label: "Event logistics", icon: Truck },
            { to: "/admin/returns", label: "Changes & cancellations", icon: UndoDot },
        ],
    },
    {
        label: "Clients",
        links: [
            { to: "/admin/customers", label: "Client list", icon: Users },
            { to: "/admin/reviews", label: "Testimonials", icon: Star },
            { to: "/admin/support", label: "Event enquiries", icon: MessageCircle },
            { to: "/admin/loyalty", label: "Client rewards", icon: Gift },
        ],
    },
    {
        label: "Marketing",
        links: [
            { to: "/admin/coupons", label: "Coupons", icon: Tag },
            { to: "/admin/banners", label: "Banners", icon: ImageIcon },
            { to: "/admin/reels", label: "Event films", icon: Film },
            { to: "/admin/campaigns", label: "Client campaigns", icon: Mail },
        ],
    },
    {
        label: "Content",
        links: [
            { to: "/admin/site-content", label: "Pages & content", icon: FileText },
            { to: "/admin/blog", label: "Event journal", icon: FileText },
            { to: "/admin/menus", label: "Menus", icon: Network },
            { to: "/admin/seo", label: "SEO", icon: Sparkles },
        ],
    },
    {
        label: "Operations",
        links: [
            { to: "/admin/suppliers", label: "Suppliers", icon: Building2 },
            { to: "/admin/purchase-orders", label: "Purchase orders", icon: PackageSearch },
        ],
    },
    {
        label: "Reports",
        links: [
            { to: "/admin/reports/sales", label: "Sales reports", icon: LineChart },
            { to: "/admin/reports/inventory", label: "Inventory reports", icon: BarChart3 },
            { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
        ],
    },
    {
        label: "Settings",
        links: [
            { to: "/admin/settings", label: "Store settings", icon: SettingsIcon },
            { to: "/admin/settings?tab=shipping", label: "Shipping", icon: Truck },
            { to: "/admin/settings?tab=payments", label: "Payments", icon: BadgeIndianRupee },
            { to: "/admin/roles", label: "User roles", icon: UserCog },
            { to: "/admin/activity", label: "Activity logs", icon: Activity },
        ],
    },
];

function SidebarSection({ section, isCollapsed }) {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-1">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-mono-ui tracking-[0.18em] uppercase text-[#857C72] hover:text-[#1B1B1F]"
                data-testid={`sidebar-section-${section.label.toLowerCase()}`}
            >
                {section.label}
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
            </button>
            {open && (
                <div className="space-y-0.5 mt-0.5">
                    {section.links.map((l) => {
                        const Icon = l.icon;
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                                        isActive
                                            ? "bg-[#1B1B1F] text-white"
                                            : "text-[#3D3934] hover:bg-[#FFFCF7]"
                                    }`
                                }
                                data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}
                            >
                                <Icon className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{l.label}</span>
                            </NavLink>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <div className="admin-shell min-h-screen bg-[#FAF7F1] flex">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-[#E7DED2] sticky top-0 h-screen">
                <div className="px-5 py-5 border-b border-[#E7DED2] flex items-center gap-2">
                    <img src={LOGO_BLACK} alt="X-Plosiv" className="h-11 w-[150px] object-contain object-left" />
                    <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-[10px] font-mono-ui">
                        ADMIN
                    </span>
                </div>
                <nav className="flex-1 overflow-y-auto px-2 py-3">
                    {SECTIONS.map((s) => (
                        <SidebarSection key={s.label} section={s} />
                    ))}
                </nav>
                <div className="border-t border-[#E7DED2] p-3 space-y-1">
                    <button
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs font-mono-ui text-[#3D3934] hover:bg-[#FFFCF7]"
                    >
                        <Eye className="h-3.5 w-3.5" /> View storefront
                    </button>
                    <button
                        onClick={() => {
                            logout();
                            navigate("/");
                        }}
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs font-mono-ui text-red-600 hover:bg-red-50"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile sidebar overlay */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
                    <aside className="relative w-72 bg-white flex flex-col h-full overflow-hidden">
                        <div className="px-5 py-5 border-b border-[#E7DED2] flex items-center gap-2">
                            <img src={LOGO_BLACK} alt="X-Plosiv" className="h-10 w-[140px] object-contain object-left" />
                            <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-[10px] font-mono-ui">ADMIN</span>
                        </div>
                        <nav className="flex-1 overflow-y-auto px-2 py-3" onClick={() => setOpen(false)}>
                            {SECTIONS.map((s) => (
                                <SidebarSection key={s.label} section={s} />
                            ))}
                        </nav>
                    </aside>
                </div>
            )}

            <div className="flex-1 flex flex-col min-w-0">
                <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-[#E7DED2] bg-white sticky top-0 z-30">
                    <button onClick={() => setOpen(true)} aria-label="Open menu" className="h-9 w-9 rounded-full bg-[#FFFCF7] border border-[#E7DED2] flex items-center justify-center">
                        <MenuIcon className="h-4 w-4" />
                    </button>
                    <img src={LOGO_BLACK} alt="X-Plosiv" className="h-9 w-[130px] object-contain object-left" />
                    <span className="ml-auto text-[10px] font-mono-ui px-2 py-1 rounded-full bg-[#FFFCF7] border border-[#E7DED2]">ADMIN</span>
                </div>
                <AdminTopBar user={user} />
                <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px] w-full">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
